import { createContext, useCallback, useEffect, useLayoutEffect, useRef, useState, type MouseEvent, type PointerEvent } from 'react';

/** ROWS NEVER MOVE UNDER THE POINTER (ported 2026-10-18 from the medrec console's post-ship review, its act-dom.ts leaveGently and instant
 *  mode; the consoles stay matched). A press on a Queue row (a typed fact, Approve, Send LOR) makes the row the system's, so the next read
 *  takes it out of its group and every row under it slides up a row: the next row's button arrives under a pointer that never moved,
 *  already hovered, and a quick second click acts on a different row (an Approve there writes another case's claim number). So while the
 *  pointer is in the table a newer answer for the SAME list waits: the rows stay as they are until the pointer leaves the table. A different
 *  list (another tab, filter, sort, search or page) shows at once: a person asked for it. A person without a pointer (the keyboard) is never
 *  held. Unlike the medrec console, whose hold a press arms, any newer answer waits here: several claims openers work one Queue, so a
 *  co-worker's save moves rows too.
 *
 *  The medrec console's Opus review of its PR #183 (10-18), three lessons kept here:
 *   - a touch is not a pointer resting in the table: a browser fires pointerleave after every lift, so a touch holds the list for
 *     `TOUCH_HOLD_MS` after its last tap instead (only a mouse's leave ends a hold);
 *   - at `HOLD_MAX_MS` a list held under a mouse that has not moved still waits, for the mouse's next move in the table, never changing
 *     under it;
 *   - a pointer click on a row's button or link in the first `CLICK_GUARD_MS` after the rows changed is dropped (`useRowsChangedGuard`):
 *     whatever moved, whoever changed it, the click was aimed at what stood there before. A keyboard press is never dropped.
 *  Pure state and hooks; nothing here reads or sends anything. */

/** The longest a newer answer waits while a mouse rests in the table; past it, it waits only for the mouse's next move. */
export const HOLD_MAX_MS = 30_000;
/** How long a touch holds the list after its last tap (a write's read lands one to three seconds after the press). */
export const TOUCH_HOLD_MS = 4_000;
/** How long after the rows changed a pointer click on a row's button or link is dropped. */
export const CLICK_GUARD_MS = 500;

/** `firm`: the value is the list's own answer, not the last list's rows drawn while a new list loads (TanStack's placeholder; All unopened
 *  keeps the old page on screen while a sort, a filter or a page reads), which is never held over: the answer a person asked for shows at
 *  once. */
export interface Held<T> { value: T; key: string; heldSince: number | null; firm: boolean }

/** ONE STEP: what the list shows, given what it showed (`s`), the newest answer and its list's key, whether the pointer is in the table, and
 *  when the mouse last moved there (`movedAt`): past the cap, the newest answer shows only once the mouse has moved since the cap ran out.
 *  `sameRows`: the newest answer's rows are the ones on screen (a new object, nothing would move), so it shows at once and no hold starts. */
export function holdStep<T>(s: Held<T> | null, value: T, key: string, inside: boolean, now: number, maxMs = HOLD_MAX_MS, firm = true, movedAt: number | null = null, sameRows = false): Held<T> {
  const fresh: Held<T> = { value, key, heldSince: null, firm };
  if (s === null || s.key !== key || !inside || !s.firm || !firm) return fresh;
  if (s.value === value) return s.heldSince === null ? s : fresh;
  if (sameRows) return fresh;
  if (s.heldSince === null) return { ...s, heldSince: now };
  if (now - s.heldSince >= maxMs && movedAt !== null && movedAt >= s.heldSince + maxMs) return fresh;
  return s;
}

/** THE HOOK'S STEP (`useHoldWhilePointed` calls exactly this): holdStep, told by `sigOf` whether the newest answer's rows are the ones on
 *  screen. claude-review on PR #75: the Queue rebuilds its answer object on every 2-minute poll (a new `now`, a Side filter), so by identity
 *  alone a quiet poll started the cap, and a real change after it swapped the rows on the first mouse move. */
export function heldNext<T>(s: Held<T> | null, value: T, key: string, inside: boolean, now: number, firm: boolean, movedAt: number | null, sigOf?: (v: T) => string): Held<T> {
  const sameRows = sigOf !== undefined && s !== null && s.value !== value && sigOf(s.value) === sigOf(value);
  return holdStep(s, value, key, inside, now, HOLD_MAX_MS, firm, movedAt, sameRows);
}

/** Whether a click is dropped: a pointer's (`detail` > 0; a keyboard's Enter or Space is 0), on a row's button or link, inside
 *  `CLICK_GUARD_MS` of the rows changing. */
export function dropsClick(detail: number, onRowAction: boolean, now: number, changedAt: number | null, ms = CLICK_GUARD_MS): boolean {
  return detail > 0 && onRowAction && changedAt !== null && now - changedAt < ms;
}

/** A row's button or link: inside a list's rows (never its header's sort presses). */
function rowActionOf(target: EventTarget | null, root: Element): boolean {
  const el = target instanceof Element ? target.closest('button, a[href], [role="button"]') : null;
  return el !== null && root.contains(el) && el.closest('thead') === null && el.closest('tbody, [data-row]') !== null;
}

/** THE CLICK GUARD: `sig` says what the rows are (their order and what each says); the first one drawn is not a change. Returns the list's
 *  capture-phase click handler. */
export function useRowsChangedGuard(sig: string): (e: MouseEvent<HTMLElement>) => void {
  const last = useRef<string | null>(null);
  const changedAt = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (last.current !== null && last.current !== sig) changedAt.current = performance.now();
    last.current = sig;
  }, [sig]);
  return useCallback((e: MouseEvent<HTMLElement>) => {
    if (!dropsClick(e.detail, rowActionOf(e.target, e.currentTarget), performance.now(), changedAt.current)) return;
    e.preventDefault();
    e.stopPropagation();
  }, []);
}

/** The list area's handlers: the hold's pointer handlers and the click guard. */
export interface HoldArea {
  onPointerEnter: (e: PointerEvent<HTMLElement>) => void;
  onPointerLeave: (e: PointerEvent<HTMLElement>) => void;
  onPointerMove: (e: PointerEvent<HTMLElement>) => void;
  onPointerUp: (e: PointerEvent<HTMLElement>) => void;
  onClickCapture: (e: MouseEvent<HTMLElement>) => void;
}

/** The list a table shows (`shown`), whether a newer answer waits (`held`), and the table's handlers (`area`). `key` names the list a person
 *  asked for (its tab, filters, sort, search, page); `placeholder`: `value` is the last list's rows while this one loads (`firm`); `sigOf`
 *  says what a shown list's rows are, for the click guard. */
export function useHoldWhilePointed<T>(value: T, key: string, placeholder = false, sigOf?: (v: T) => string): { shown: T; held: boolean; area: HoldArea } {
  const [mouseIn, setMouseIn] = useState(false);
  const [touchIn, setTouchIn] = useState(false);
  const [, wake] = useState(0);
  const touchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const movedAt = useRef<number | null>(null);
  const st = useRef<Held<T> | null>(null);
  // what an answer's rows are, worked out once per answer and list (a filter changes the key, and with it what the rows are)
  const sigs = useRef<{ key: string; of: WeakMap<object, string> }>({ key, of: new WeakMap() });
  if (sigs.current.key !== key) sigs.current = { key, of: new WeakMap() };
  const sigFor = (v: T): string => {
    if (!sigOf) return '';
    if (typeof v !== 'object' || v === null) return sigOf(v);
    const hit = sigs.current.of.get(v);
    if (hit !== undefined) return hit;
    const sig = sigOf(v);
    sigs.current.of.set(v, sig);
    return sig;
  };
  st.current = heldNext(st.current, value, key, mouseIn || touchIn, Date.now(), !placeholder, movedAt.current, sigOf ? sigFor : undefined);
  const held = st.current.value !== value;
  const heldSince = st.current.heldSince;
  useEffect(() => () => { if (touchTimer.current !== null) clearTimeout(touchTimer.current); }, []);
  const touchFor = (ms: number) => {
    if (touchTimer.current !== null) clearTimeout(touchTimer.current);
    setTouchIn(true);
    touchTimer.current = setTimeout(() => { touchTimer.current = null; setTouchIn(false); }, ms);
  };
  const guard = useRowsChangedGuard(sigFor(st.current.value));
  const area: HoldArea = {
    onPointerEnter: (e) => { if (e.pointerType === 'mouse') setMouseIn(true); else touchFor(TOUCH_HOLD_MS); },
    // a touch's lift fires pointerleave: only a mouse leaving ends the hold
    onPointerLeave: (e) => { if (e.pointerType === 'mouse') setMouseIn(false); },
    onPointerMove: (e) => {
      if (e.pointerType !== 'mouse') return;
      const t = Date.now();
      movedAt.current = t;
      // past the cap, this move is the one the newest answer waited for
      if (held && heldSince !== null && t - heldSince >= HOLD_MAX_MS) wake((n) => n + 1);
    },
    onPointerUp: (e) => { if (e.pointerType !== 'mouse') touchFor(TOUCH_HOLD_MS); },
    onClickCapture: guard,
  };
  return { shown: st.current.value, held, area };
}

/** A HELD ROW THE NEWEST ANSWER NO LONGER HAS AS IT IS (it left, moved to another group, or changed): it stays in its place but is inert and
 *  dimmed until the pointer leaves, so it can never be pressed twice (a second claim number on a row the system is already writing; the
 *  medrec console's stand-in rows are inert the same way). A list provides it from its newest answer; a row asks it with its signature. */
export const HeldGoneContext = createContext<(sig: string) => boolean>(() => false);
